import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { useI18n } from "../../context/I18nContext";

const KEY = "omnia_cookie_consent";
const ease = [0.16, 1, 0.3, 1];

export default function CookieBanner() {
  const { t } = useI18n();
  const location = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(KEY);
    if (!saved) setShow(true);
  }, []);

  const decide = (choice) => {
    localStorage.setItem(KEY, choice);
    setShow(false);
  };

  // Only relevant on the public site — never over the logged-in OS workspace
  // nor over the auth pages (the form occupies the right column and the banner
  // would overlap/intercept the submit buttons, e.g. the MFA step).
  const AUTH_PATHS = ["/login", "/register", "/register-company", "/forgot-password", "/forgot-username", "/reset-password"];
  if (location.pathname.startsWith("/os") || AUTH_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease }}
          data-testid="cookie-banner"
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-md z-[60]">
          <div className="rounded-2xl bg-navy text-cream shadow-2xl shadow-navy/30 p-6 grain">
            <div className="flex items-center gap-2 mb-3">
              <Cookie className="w-4 h-4 text-gold" />
              <span className="overline text-cream/50">{t("cookies.title")}</span>
            </div>
            <p className="text-sm text-cream/75 leading-relaxed">
              {t("cookies.body")}{" "}
              <Link to="/privacidad" data-testid="cookie-privacy-link" className="text-gold underline underline-offset-2 hover:text-gold/80 transition-colors">
                {t("cookies.more")}
              </Link>.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button data-testid="cookie-accept" onClick={() => decide("accepted")}
                className="flex-1 rounded-full bg-gradient-violet text-white text-sm py-2.5 hover:opacity-90 transition-opacity">
                {t("cookies.accept")}
              </button>
              <button data-testid="cookie-reject" onClick={() => decide("rejected")}
                className="flex-1 rounded-full border border-cream/25 text-cream/80 text-sm py-2.5 hover:border-cream/50 hover:text-cream transition-colors">
                {t("cookies.reject")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
